/*------------------------------------------------------------------------------
   About      : Access Groups tab — System (Blueprint) vs Custom, never mixed

   Classification: backend `is_system` flag, set once at group-creation time
   (proxypulse discovery.get_or_create_feature_group for blueprint-discovered
   groups, false for anything created via POST /feature-groups) — see
   selectSystemGroups/selectCustomGroups in rbacSlice.ts. The ui_registry
   group is hidden from both (managed implicitly by the Pages & Actions panel).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useState, SyntheticEvent } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectSystemGroups, selectCustomGroups } from 'store/dataReducers/rbacSlice';
import { FeatureGroupTreePanel } from './FeatureGroupTreePanel';

type GroupTab = 'system' | 'custom';

const TAB_CAPTIONS: Record<GroupTab, string> = {
  custom : 'Groups you create to bundle API permissions for assignment — create, edit membership, delete.',
  system : 'Auto-discovered from backend blueprints. Membership is managed by the backend — read-only here.',
};

export const AccessGroupsPanel = () => {
  const [tab, setTab] = useState<GroupTab>('custom');

  const systemGroups = useSelector(selectSystemGroups);
  const customGroups = useSelector(selectCustomGroups);

  const handleTabChange = (_: SyntheticEvent, value: GroupTab) => setTab(value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Custom (${customGroups.length})`} value="custom" />
        <Tab label={`System (${systemGroups.length})`} value="system" />
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
        {TAB_CAPTIONS[tab]}
      </Typography>
      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
        {tab === 'custom' ? (
          <FeatureGroupTreePanel fullWidth groups={customGroups} />
        ) : (
          <FeatureGroupTreePanel fullWidth groups={systemGroups} readOnly />
        )}
      </Box>
    </Box>
  );
};
